import { gql } from "lib/apolloClient";
import { attachmentFragment } from "modules/CommonFragments";
import {
  questionAttachmentFragments,
  organizationResourceQuestionFragment,
} from "./WorksheetFragments";
import { entityEventTrackFragment } from "ClassRoom/modules/ClassRoomFragments";

export const createQuestionMutation = gql`
  mutation createQuestion($input: CreateQuestionInput!) {
    content {
      createQuestion(input: $input) {
        id
      }
    }
  }
`;

/**
 * @deprecated Use `updateQuestionMutation` instead.
 * This mutation is kept for backward compatibility.
 * It will be removed in future versions.
 */
export const updateOrganizationResourceMutation = gql`
  mutation updateOrganizationResource(
    $input: OrganizationResourceUpdateInput!
  ) {
    platform {
      updateOrganizationResource(input: $input) {
        id
      }
    }
  }
`;

export const updateQuestionMutation = gql`
  mutation updateQuestion($input: UpdateQuestionInput!) {
    content {
      updateQuestion(input: $input) {
        id
      }
    }
  }
`;

export const createWorksheetSubmissionMutation = gql`
  mutation createWorksheetSubmission($parentId: ID!, $assessmentId: ID!) {
    content {
      createWorksheetSubmission(
        input: { parentId: $parentId, assessmentId: $assessmentId }
      ) {
        id
      }
    }
  }
`;

export const updateQuestionSubmissionMutation = gql`
  mutation updateQuestionSubmission(
    $input: UpdateQuestionSubmissionInput!
    $fetchFields: Boolean = false
  ) {
    content {
      updateQuestionSubmission(input: $input) {
        id
        fields @include(if: $fetchFields) {
          id
          resolveFields {
            id
            ... on QuestionAttachment {
              id
              attachments {
                ...questionAttachmentItem
              }
            }
          }
        }
      }
    }
  }
  ${questionAttachmentFragments.questionAttachment}
`;

/**
 * @deprecated use reorderQuestionMutation instead
 * This is kept for backward compatibility
 * It will be removed in future versions
 */
export const reorderOrganizationResourceMutation = gql`
  mutation reorderOrganizationResource(
    $id: ID
    $putAfterItemId: ID
    $switchParent: Boolean
    $parentResourceId: ID
  ) {
    platform {
      reorderOrganizationResource(
        input: {
          id: $id
          putAfterItemId: $putAfterItemId
          switchParent: $switchParent
          parentResourceId: $parentResourceId
        }
      )
    }
  }
`;

export const createQuestionTopicMutation = gql`
  mutation createQuestionTopic($input: CreateQuestionTagInput!) {
    content {
      createQuestionTag(input: $input) {
        id
      }
    }
  }
`;

export const addQuestionToSchoolLibraryMutation = gql`
  mutation addQuestionToSchoolLibrary(
    $input: AddQuestionToSchoolLibraryInput!
  ) {
    content {
      addQuestionToSchoolLibrary(input: $input) {
        isSuccess
      }
    }
  }
`;

/**
 * @deprecated Use `importQuestionFromSchoolLibraryMutationV2` instead.
 * This function is kept for backward compatibility.
 * It will be removed in future versions.
 */
export const importQuestionFromSchoolLibraryMutation = gql`
  mutation importQuestionFromSchoolLibrary(
    $input: ImportQuestionFromSchoolLibraryInput!
  ) {
    content {
      importQuestionFromSchoolLibrary(input: $input) {
        isSuccess
        questionIds
      }
    }
  }
`;

export const importQuestionFromToddleLibraryMutation = gql`
  mutation importQuestionFromToddleLibrary(
    $input: ImportQuestionFromToddleLibraryInput!
  ) {
    content {
      importQuestionFromToddleLibrary(input: $input) {
        isSuccess
      }
    }
  }
`;

export const createWorksheetQuestionsUsingAI = gql`
  mutation createWorksheetQuestionsUsingAI(
    $input: CreateWorksheetQuestionsUsingAIInput!
  ) {
    content {
      createWorksheetQuestionsUsingAI(input: $input) {
        id
      }
    }
  }
`;

export const createWorksheetQuestionUsingAIV2 = gql`
  mutation createWorksheetQuestionsUsingAiV2(
    $input: CreateWorksheetQuestionsUsingAIInputV2!
  ) {
    content {
      createWorksheetQuestionsUsingAiV2(input: $input) {
        id
      }
      __typename
    }
  }
`;

/**
 * @deprecated Use `duplicateQuestionInWorksheetV2Mutation` instead.
 * This mutation is kept for backward compatibility.
 * It will be removed in future versions.
 */
export const duplicateQuestionInWorksheetMutation = gql`
  mutation duplicateQuestionInWorksheet(
    $input: DuplicateQuestionInWorksheetInput!
  ) {
    content {
      duplicateQuestionInWorksheet(input: $input) {
        isSuccess
        questionResource {
          ...organizationResourceQuestionItem
        }
      }
    }
  }
  ${organizationResourceQuestionFragment}
`;

/**
 * @deprecated Use `replaceWorksheetQuestionUsingAIV2Mutation` instead.
 * This is kept for backward compatibility.
 * It will be removed in future versions.
 */
export const replaceWorksheetQuestionUsingAI = gql`
  mutation replaceWorksheetQuestionUsingAI(
    $input: ReplaceWorksheetQuestionInput!
  ) {
    content {
      replaceWorksheetQuestionGeneratedFromAI(input: $input) {
        id
      }
    }
  }
`;

export const startSafeExamBrowserMutation = gql`
  mutation startSafeExamBrowserWorksheet($input: SafeExamBrowserUrlInput) {
    content {
      startSafeExamBrowserWorksheet(input: $input) {
        isSuccess
        url
        warning
      }
    }
  }
`;

export const aiExtractQuestionsMutation = gql`
  mutation aiExtractQuestions($input: AiExtractQuestionsInput!) {
    content {
      aiExtractQuestions(input: $input) {
        isSuccess
      }
      __typename
    }
  }
`;

export const createQuestionMutationV2 = gql`
  mutation createQuestionV2($input: CreateWorksheetQuestionInputV2!) {
    content {
      createQuestionV2(input: $input) {
        id
      }
    }
  }
`;

export const reorderQuestionMutation = gql`
  mutation reorderQuestion(
    $questionId: ID!
    $putAfterQuestionId: ID
    $parentId: ID!
  ) {
    content {
      reorderQuestion(
        input: {
          questionId: $questionId
          putAfterQuestionId: $putAfterQuestionId
          parentId: $parentId
        }
      )
    }
  }
`;

export const updateQuestionOrderMutation = gql`
  mutation worksheetRaceConditionHandling(
    $input: worksheetRaceConditionHandlingInput!
  ) {
    content {
      worksheetAiPostProcessing(input: $input)
    }
  }
`;

export const deleteQuestionMutation = gql`
  mutation deleteQuestion($id: ID!) {
    content {
      deleteQuestion(input: { id: $id })
    }
  }
`;

export const importQuestionFromSchoolLibraryMutationV2 = gql`
  mutation importQuestionFromSchoolLibraryV2(
    $input: ImportQuestionToWorksheetV2Input!
  ) {
    content {
      importQuestionFromSchoolLibraryV2(input: $input) {
        isSuccess
        questionIds
      }
    }
  }
`;

export const importQuestionFromToddleLibraryMutationV2 = gql`
  mutation importQuestionFromToddleLibraryV2(
    $input: ImportQuestionToWorksheetV2Input!
  ) {
    content {
      importQuestionFromToddleLibraryV2(input: $input) {
        isSuccess
        questionIds
      }
    }
  }
`;

export const duplicateQuestionInWorksheetV2Mutation = gql`
  mutation duplicateQuestionInWorksheetV2(
    $input: ImportQuestionToWorksheetV2Input!
  ) {
    content {
      duplicateQuestionInWorksheetV2(input: $input) {
        isSuccess
        questionIds
      }
    }
  }
`;

export const replaceWorksheetQuestionUsingAIV2Mutation = gql`
  mutation replaceWorksheetQuestionUsingAIV2(
    $input: ReplaceWorksheetQuestionInputV2!
  ) {
    content {
      replaceWorksheetQuestionGeneratedFromAIV2(input: $input) {
        id
      }
    }
  }
`;

export const addBulkStudentResponseToPortfolioMutation = gql`
  mutation addBulkStudentResponseToPortfolio(
    $input: AddBulkStudentResponseToPortfolioInput!
  ) {
    content {
      addBulkStudentResponseToPortfolio(input: $input) {
        isSuccess
        warning
      }
    }
  }
`;

export const downloadBulkStudentResponseMutation = gql`
  mutation downloadBulkStudentResponse(
    $input: DownloadBulkStudentResponseInput!
  ) {
    content {
      downloadBulkStudentResponse(input: $input) {
        isSuccess
      }
    }
  }
`;

export const createEntityEventTrackingMutation = gql`
  mutation createEntityEventTracking(
    $input: [CreateEntityEventTrackingInput!]!
  ) {
    platform {
      createEntityEventTracking(input: $input) {
        response {
          ...entityEventTrackItem
        }
        warning
      }
    }
  }
  ${entityEventTrackFragment.entityEventTrack}
`;

export const createAttachmentsMutation = gql`
  mutation createAttachments($input: [AttachmentInput!]) {
    platform {
      createAttachment(input: $input) {
        ...attachmentItem
      }
    }
  }
  ${attachmentFragment.attachment}
`;

export const deleteAttachmentMutation = gql`
  mutation deleteAttachments(
    $attachmentIds: [ID]!
    $deleteCondition: DeleteAttachmentConditionInput
    $deleteFromKnowledgeBase: Boolean
  ) {
    platform {
      deleteAttachments(
        attachmentIds: $attachmentIds
        deleteCondition: $deleteCondition
        deleteFromKnowledgeBase: $deleteFromKnowledgeBase
      ) {
        isSuccess
        warning
      }
    }
  }
`;
